/**
 * The cull corridor — spec §7 beat 5, and the game's centre of gravity.
 *
 * Every room before this one is a place animals are kept or a passage people
 * walk. This is the first room that is **plant**: a straight run of concrete
 * beside a bay where a machine works, and the only reason the run exists is to
 * get things from one end of it to the other.
 *
 * ## Why it is a straight line with nothing in it
 *
 * The service corridor's three bays were its cover and therefore its whole
 * stealth grammar. This room has **no cover, because it has no watcher** — see
 * `docs/beat-5-plan.md` rule 1. Removing the last hiding place from a player who
 * has just been taught to find one is the beat's entire mechanic, and it is
 * subtraction, not addition: no patrol, no cone, no suspicion, no alarm.
 *
 * What is left is a long walk toward one red lamp, past a slot of cold light
 * with shapes hanging in it, on a machine's clock that never once reacts to her.
 *
 * ## The bay is wider than its mouth, and that is the shot
 *
 * The sheeting covers 2.4 m. Behind it the bay is 6.8 m across, and the rail of
 * hanging shapes runs the whole width and out of sight at both ends. **What she
 * can see is a slice of something that continues.** A bay the size of its own
 * doorway would have shown the player the end of it, and the end of it is the
 * one thing this room must never show.
 *
 * ## Collision comes from the same list as the meshes
 *
 * D-089, twice over: this room's ancestor shipped two voids — three bays with no
 * floor and an exit that was a hole — because a room authored as absences in a
 * wall list assumes the floor. Everything here is a `HallSolid` in
 * `cullCorridorSolids()`, drawn and collided from one array, and the bay's
 * kerb-and-sheeting barrier is in it: she can see into the bay and she can never
 * enter it.
 */

import * as THREE from 'three';
import { plasticSheeting } from '@/models/env/plastic-sheeting';
import { MACHINE_CENTRE_OFFSET } from '@/models/env/plant-machine';
import { getAsset } from '@/models/registry';
import type { AssetDefinition } from '@/models/types';
import { getZone, type ZonePalette } from '@/render/palette';
import type { Fixture } from '@/render/lighting';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';

/** The zone this room is in. Everything else reads its palette from here. */
export const CULL_ZONE_ID = 'cull';

/**
 * Every authored dimension of the corridor, in metres.
 *
 * Wider and taller than the service corridor (3.2 × 3.0 against 2.4 × 2.6),
 * because things are pushed down this one and because the rail behind the bay
 * needs headroom. The extra half metre of ceiling is most of what makes the
 * room read as plant rather than as passage.
 */
export const CULL = {
  floorY: 0,
  ceiling: 3.0,
  wallThickness: 0.2,
  minX: -1.6,
  maxX: 1.6,
  minZ: -13.0,
  maxZ: 13.0,
} as const;

/**
 * The bay behind the east wall: three metres deep and nearly seven wide.
 *
 * `mouthMinZ`/`mouthMaxZ` are the sheeting, and they are deliberately a third of
 * the bay's width. See the header.
 */
export const CULL_BAY = {
  minX: CULL.maxX,
  maxX: CULL.maxX + 3.0,
  minZ: -3.4,
  maxZ: 3.4,
  mouthMinZ: -1.2,
  mouthMaxZ: 1.2,
  /** Head height of the sheeting, and the height of the mouth it hangs in. */
  mouthHeight: 2.6,
  /**
   * The kerb across the mouth.
   *
   * It is the reason she cannot walk into the bay, and it is also why she does
   * not have to be *stopped* by a scripted refusal: a 0.25 m lip is a kerb to a
   * man and a wall to a three-month-old pig, which is D-012's rule for the
   * fourth time. The sheeting above it is solid to her as well — plastic she
   * cannot see through is not the same as plastic she can push past.
   */
  kerbHeight: 0.25,
} as const;

/** Where she comes in: through the door beat 4 ends at, facing the length of it. */
export const CULL_SPAWN = { x: 0, y: 0.25, z: -12.0, facing: 0 } as const;

/** A room state, not a beat number: other returns can reuse this ordinary work dressing. */
export interface CullCorridorOptions {
  readonly returnState?: boolean;
}

/**
 * The `personnel-door` leaf, as the asset builds it: hinge on the left jamb,
 * 0.9 m wide, 2.05 m tall. Restated for the return state's open-leaf solid.
 */
const CULL_DOOR_LEAF = { hingeX: -0.45, width: 0.9, height: 2.05 } as const;

/**
 * How far the leaf stands open in beat 8's return: a right angle, swung into
 * the corridor, the same side beat 5's director opens it toward.
 */
const CULL_RETURN_DOOR_OPEN = Math.PI / 2;

/** Half the far doorway's width, and the height of its opening. A door for boots. */
const CULL_DOORWAY_HALF = 0.55;
const CULL_DOORWAY_HEIGHT = 2.1;

/**
 * The passage beyond the far door: bounded, nearly dark, and exactly as long as
 * the shot needs.
 *
 * Nine metres, and the number is the ride rather than the architecture. The bin
 * stands beside the door, so everything on this side of it is over in a second
 * and a half — the whole of the carried shot happens *after* the doorway, with
 * the red lamp receding behind and one cold worklight at the far end coming up.
 * A three-metre threshold made the beat's payoff a four-second push, which is
 * what the first run of `cull-corridor.spec.ts` measured and is the reason this
 * number is nine.
 *
 * Beat 4's vestibule exists for the other reason and was added after that beat
 * shipped with a void where its exit should be (D-089). This one is here before
 * anything has ever been pushed through the door.
 */
export const CULL_THRESHOLD = { minZ: CULL.maxZ, maxZ: CULL.maxZ + 9.0, halfX: 1.4 } as const;

/**
 * Beat 8 enters from the bounded threshold the bin left through, then faces the
 * corridor in reverse. Both the spawn and exit derive from the same authored
 * shell as beat 5, so moving that threshold cannot strand the return.
 */
export const CULL_RETURN_SPAWN = {
  x: 0,
  y: CULL.floorY + CULL_BAY.kerbHeight,
  z: (CULL_THRESHOLD.minZ + CULL_THRESHOLD.maxZ) / 2,
  facing: Math.PI,
} as const;

/** The return finishes at the old corridor entrance rather than beyond its sealed wall. */
export const CULL_RETURN_EXIT_Z = CULL_SPAWN.z;

/**
 * The rail, and the shapes on it.
 *
 * The rail runs along Z — parallel to the mouth, not into the bay — so the line
 * of shapes crosses the slot and leaves it at both ends. Eight of them at 0.55 m
 * covers 3.85 m against a 2.4 m mouth: from the corridor she can see four, and
 * the ones she cannot see are the point.
 */
export const CULL_RAIL = {
  x: CULL.maxX + 1.8,
  y: 2.15,
  minZ: -2.2,
  count: 8,
  pitch: 0.55,
  /**
   * The link's hang height below this rail, expressed as the shape asset's
   * ground-contact origin above the floor. It belongs in placement data because
   * `docs/asset-conventions.md` requires the reusable mesh to start at ground
   * contact; baking it into the mesh made the old cull-bay-only variant fail
   * that contract while concealing the missing placement relationship.
   */
  shapeOriginY: 0.78,
} as const;

/** The machine, at the far end of the bay so the room's pulse comes from ahead. */
export const CULL_MACHINE_AT = { x: CULL.maxX + 1.9, z: 2.6 } as const;

/**
 * The bin she leaves in, beside the door, on her side of the corridor.
 *
 * **Moved from z = 11.2 to 9.6 in session 29**, and the 1.6 m is the room the
 * bin needs to lie down in. It topples toward the door and ends up 1.51 m long
 * in Z; at 11.2 its mouth would have finished 0.3 m short of the door leaf, with
 * nowhere for her to stand while she decided to get in. See `CULL_BIN`.
 */
export const CULL_BIN_AT = { x: -1.0, z: 9.6 } as const;

/**
 * The bin as a thing that falls over, and then as a place she can walk into.
 *
 * ── Why the beat works this way at all ──────────────────────────────────────
 *
 * It used to be one `nudge` that teleported her inside an upright bin, and Alex
 * played it and said, in as many words, that it was unrealistic. He is right and
 * the reason is worth writing down: **a 0.32 m pig cannot get into a 1.15 m bin,
 * and the game was not even pretending she had.** The verb completed and she was
 * simply somewhere else. Every other thing this game asks her to do is a thing
 * her body could actually do — she goes through the fence bay at a roll because
 * a hole 0.31 m off the ground is a window (D-012), and that rule was being
 * broken by the one beat whose whole subject is what a body is for.
 *
 * So the bin goes over first. She shoves it, it falls, and *then* there is an
 * opening at floor height that a small animal can walk into — which is a hole
 * she is the right size for, which is the game's own grammar, and which is also
 * the only version of this that lets the player choose to get in.
 *
 * ── The numbers ─────────────────────────────────────────────────────────────
 *
 * The asset is 0.72 wide, 1.15 tall, 0.62 long, and it is placed `rotY = π/2`,
 * so its 0.72 runs along world Z and its 0.62 across the corridor.
 *
 * `tipPivotDz` is half of that 0.72: the pivot is the downstream bottom edge.
 * Rotating +π/2 about world X takes the top face from +Y to +Z, so the toppled
 * bin lies between `z + 0.36` and `z + 0.36 + 1.15`, stands 0.72 off the floor,
 * and its **mouth faces the door**.
 *
 * That it faces away from her is deliberate and it is the second half of the
 * point: she has to walk around it. A mouth that landed pointing back at her
 * would be a mouth she was already standing inside — the topple sweeps through
 * exactly the place the `nudge` reach puts her — and getting in would once again
 * be something that happened to her rather than something she did.
 */
export const CULL_BIN = {
  /** Half the bin's Z extent once placed. The topple axis is this far downstream. */
  tipPivotDz: 0.36,
  /** How far the mouth reaches past the pivot once it is down. The bin's height. */
  tipLength: 1.15,
  /** How wide the shell is across the corridor, and how tall it lies. */
  halfX: 0.31,
  lyingHeight: 0.72,
} as const;

/**
 * The volume that counts as being inside the toppled bin.
 *
 * Inset from the shell on every side so that clipping the outside of it is not
 * the same thing as being in it: 0.05 m in X, and the near face starts 0.14 m
 * inside the mouth so she is past the rim rather than at it.
 *
 * `binZ` is the bin's own base centre, which the director moves during the ride,
 * so this is a function rather than a constant — the bin is only ever entered
 * while it is stationary, but a volume that silently stopped tracking its own
 * bin is the `CULL_RAIL.y` defect waiting to happen again.
 */
export function cullBinInterior(binZ: number): {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
} {
  const nearZ = binZ + CULL_BIN.tipPivotDz;
  return {
    min: { x: CULL_BIN_AT.x - CULL_BIN.halfX + 0.05, y: 0, z: nearZ + 0.14 },
    max: {
      x: CULL_BIN_AT.x + CULL_BIN.halfX - 0.05,
      y: CULL_BIN.lyingHeight,
      z: nearZ + CULL_BIN.tipLength - 0.14,
    },
  };
}

/** The far door. Shut for the whole beat; it opens once, from the other side. */
export const CULL_DOOR_AT = { x: 0, z: CULL.maxZ } as const;

/**
 * The one red lamp, over the door, on the east wall.
 *
 * Spec §7 gives this room "one red lamp" and the palette gives it one accent.
 * Both are singular and this is the only place either is spent: nothing else in
 * the room may carry `0xc02b1f`, and there is exactly one coloured `Fixture`.
 */
export const CULL_LAMP_AT = { x: CULL.maxX - 0.12, y: 2.4, z: 12.2 } as const;

const zone: ZonePalette = (() => {
  const found = getZone(CULL_ZONE_ID);
  if (!found) throw new Error('cull corridor: cull zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`cull corridor: surface ${index} missing from cull palette`);
  return colour;
}

const CONCRETE_DARK = zoneSurface(0);
const CONCRETE_FLOOR = zoneSurface(1);
const CONCRETE_WALL = zoneSurface(2);
const STEEL = zoneSurface(3);

function spanCentre(min: number, max: number): number {
  return (min + max) / 2;
}

function spanHalf(min: number, max: number): number {
  return (max - min) / 2;
}

function shellBox(
  name: string,
  half: HallSolid['half'],
  pos: HallSolid['pos'],
  colour: number,
  kind: HallSolid['kind'] = 'mineral',
): HallSolid {
  return { name, half, pos, colour, kind };
}

function assetDefinitionOrThrow(id: string): AssetDefinition {
  const asset = getAsset(id);
  if (!asset) throw new Error(`cull corridor: asset "${id}" is not registered`);
  return asset;
}

function assetOrThrow(id: string): THREE.Group {
  return assetDefinitionOrThrow(id).create();
}

/**
 * Every box in the room, mesh and collider alike.
 *
 * The bay's own shell is in here rather than in the dressing for the reason
 * D-089 gives at the cost of a session: a bay authored as a gap in a wall is a
 * bay with no floor, and it reads in a screenshot exactly like a dark alcove.
 */
export function cullCorridorSolids(options: CullCorridorOptions = {}): HallSolid[] {
  const solids: HallSolid[] = [];
  const { ceiling, floorY, minX, maxX, minZ, maxZ, wallThickness } = CULL;
  const wallHalf = wallThickness / 2;
  const centreX = spanCentre(minX, maxX);
  const centreZ = spanCentre(minZ, maxZ);

  solids.push(
    shellBox(
      'floor-corridor',
      { x: spanHalf(minX, maxX), y: 0.1, z: spanHalf(minZ, maxZ) },
      { x: centreX, y: floorY - 0.1, z: centreZ },
      CONCRETE_FLOOR,
    ),
    shellBox(
      'ceiling-corridor',
      { x: spanHalf(minX - wallThickness, maxX + wallThickness), y: 0.1, z: spanHalf(minZ, maxZ) },
      { x: centreX, y: ceiling + 0.1, z: centreZ },
      CONCRETE_DARK,
    ),
    shellBox(
      'wall-west',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(minZ, maxZ) },
      { x: minX - wallHalf, y: ceiling / 2, z: centreZ },
      CONCRETE_WALL,
    ),
    // The far wall in two pieces around a doorway, and a bounded threshold
    // beyond it. **D-089 twice**: beat 4's exit was a hole in the world, three
    // tenths of a metre past the last floor, and the only way to finish the beat
    // was to walk out of it. The bin is pushed through this opening at the end
    // of beat 5 with the camera inside it, so what is on the other side is on
    // screen for the last four seconds of Act 1's first half.
    shellBox(
      'wall-north-west',
      { x: spanHalf(minX - wallThickness, -CULL_DOORWAY_HALF), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(minX - wallThickness, -CULL_DOORWAY_HALF), y: ceiling / 2, z: maxZ + wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-north-east',
      { x: spanHalf(CULL_DOORWAY_HALF, maxX + wallThickness), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(CULL_DOORWAY_HALF, maxX + wallThickness), y: ceiling / 2, z: maxZ + wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-north-lintel',
      { x: CULL_DOORWAY_HALF, y: spanHalf(CULL_DOORWAY_HEIGHT, ceiling), z: wallHalf },
      { x: 0, y: spanCentre(CULL_DOORWAY_HEIGHT, ceiling), z: maxZ + wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-south-end',
      { x: spanHalf(minX - wallThickness, maxX + wallThickness), y: ceiling / 2, z: wallHalf },
      { x: centreX, y: ceiling / 2, z: minZ - wallHalf },
      CONCRETE_WALL,
    ),
  );

  // The east wall in three pieces, with the mouth as an absence between two of
  // them — and, unlike beat 4's bays, the absence is immediately filled in by
  // the kerb and the sheeting barrier below. An opening she can see through and
  // cannot pass is a different object from a hole, and only one of them is what
  // this room means.
  solids.push(
    shellBox(
      'wall-east-near',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(minZ, CULL_BAY.mouthMinZ) },
      { x: maxX + wallHalf, y: ceiling / 2, z: spanCentre(minZ, CULL_BAY.mouthMinZ) },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-east-far',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(CULL_BAY.mouthMaxZ, maxZ) },
      { x: maxX + wallHalf, y: ceiling / 2, z: spanCentre(CULL_BAY.mouthMaxZ, maxZ) },
      CONCRETE_WALL,
    ),
    // The lintel over the mouth: the sheeting hangs from 2.6 and the room is
    // 3.0, so there is 0.4 m of wall above it. Without this the mouth is a slot
    // to the ceiling and the bay's cold spill washes the corridor's ceiling.
    shellBox(
      'wall-east-lintel',
      { x: wallHalf, y: spanHalf(CULL_BAY.mouthHeight, ceiling), z: spanHalf(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ) },
      { x: maxX + wallHalf, y: spanCentre(CULL_BAY.mouthHeight, ceiling), z: spanCentre(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ) },
      CONCRETE_WALL,
    ),
    shellBox(
      'bay-kerb',
      { x: wallHalf, y: CULL_BAY.kerbHeight / 2, z: spanHalf(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ) },
      { x: maxX + wallHalf, y: CULL_BAY.kerbHeight / 2, z: spanCentre(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ) },
      STEEL,
      'metal',
    ),
  );

  if (!options.returnState) {
    // The sheeting's own barrier: invisible, because the hanging panels are the
    // mesh, and full height because a strip curtain she could walk under is a
    // curtain that has to be animated out of her way. She never touches it.
    solids.push({
      name: 'bay-sheeting-solid',
      half: { x: wallHalf, y: (CULL_BAY.mouthHeight - CULL_BAY.kerbHeight) / 2, z: spanHalf(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ) },
      pos: { x: maxX + wallHalf, y: spanCentre(CULL_BAY.kerbHeight, CULL_BAY.mouthHeight), z: spanCentre(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ) },
      colour: STEEL,
      kind: 'metal',
      visible: false,
    });
    // The shut far door's own barrier. Until session 70 the doorway was a gap in
    // the wall solids with a leaf that was only a mesh, so walking straight on
    // from spawn went through the door and into the empty threshold — the "door
    // out into nothing" in `docs/playthrough-audit.md` row 1. It fills the whole
    // doorway rather than the 0.9 m leaf, so the frame is not a pair of slots.
    //
    // It never moves: the door opens only after she is in the bin, and from the
    // righting on her body is placed by `character.reset` (`cull.carrying`),
    // which does not collide. Gate: the leaf-probe walk in
    // `cold-spawn.spec.ts`, watched going red without this solid. The return
    // state walks back through this doorway, so it has none.
    solids.push({
      name: 'far-door-leaf-solid',
      half: { x: CULL_DOORWAY_HALF, y: CULL_DOORWAY_HEIGHT / 2, z: wallHalf },
      pos: { x: CULL_DOOR_AT.x, y: CULL_DOORWAY_HEIGHT / 2, z: CULL_DOOR_AT.z + wallHalf },
      colour: STEEL,
      kind: 'metal',
      visible: false,
    });
  }

  if (options.returnState) {
    // The open leaf's own solid, where the return dressing draws it: standing at
    // a right angle into the corridor from the hinge jamb. Without it she can
    // brush through the leaf beside the path out.
    solids.push({
      name: 'far-door-open-leaf-solid',
      half: { x: 0.03, y: CULL_DOOR_LEAF.height / 2, z: CULL_DOOR_LEAF.width / 2 },
      pos: { x: CULL_DOOR_AT.x + CULL_DOOR_LEAF.hingeX, y: CULL_DOOR_LEAF.height / 2, z: CULL_DOOR_AT.z - CULL_DOOR_LEAF.width / 2 },
      colour: STEEL,
      kind: 'metal',
      visible: false,
    });
  }

  // ── The bay itself, which is a room and not a gap ──────────────────────────
  const bayCentreX = spanCentre(CULL_BAY.minX, CULL_BAY.maxX);
  const bayCentreZ = spanCentre(CULL_BAY.minZ, CULL_BAY.maxZ);
  solids.push(
    shellBox(
      'floor-bay',
      { x: spanHalf(CULL_BAY.minX, CULL_BAY.maxX), y: 0.1, z: spanHalf(CULL_BAY.minZ, CULL_BAY.maxZ) },
      { x: bayCentreX, y: floorY - 0.1, z: bayCentreZ },
      CONCRETE_FLOOR,
    ),
    shellBox(
      'ceiling-bay',
      { x: spanHalf(CULL_BAY.minX, CULL_BAY.maxX + wallThickness), y: 0.1, z: spanHalf(CULL_BAY.minZ - wallThickness, CULL_BAY.maxZ + wallThickness) },
      { x: bayCentreX, y: ceiling + 0.1, z: bayCentreZ },
      CONCRETE_DARK,
    ),
    shellBox(
      'wall-bay-back',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(CULL_BAY.minZ - wallThickness, CULL_BAY.maxZ + wallThickness) },
      { x: CULL_BAY.maxX + wallHalf, y: ceiling / 2, z: bayCentreZ },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-bay-south',
      { x: spanHalf(CULL_BAY.minX, CULL_BAY.maxX), y: ceiling / 2, z: wallHalf },
      { x: bayCentreX, y: ceiling / 2, z: CULL_BAY.minZ - wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-bay-north',
      { x: spanHalf(CULL_BAY.minX, CULL_BAY.maxX), y: ceiling / 2, z: wallHalf },
      { x: bayCentreX, y: ceiling / 2, z: CULL_BAY.maxZ + wallHalf },
      CONCRETE_WALL,
    ),
  );

  // ── The threshold beyond the door ─────────────────────────────────────────
  const thresholdCentreZ = spanCentre(CULL_THRESHOLD.minZ, CULL_THRESHOLD.maxZ);
  solids.push(
    shellBox(
      'floor-threshold',
      { x: CULL_THRESHOLD.halfX, y: 0.1, z: spanHalf(CULL_THRESHOLD.minZ, CULL_THRESHOLD.maxZ) },
      { x: 0, y: floorY - 0.1, z: thresholdCentreZ },
      CONCRETE_FLOOR,
    ),
    shellBox(
      'ceiling-threshold',
      { x: CULL_THRESHOLD.halfX + wallThickness, y: 0.1, z: spanHalf(CULL_THRESHOLD.minZ, CULL_THRESHOLD.maxZ + wallThickness) },
      { x: 0, y: ceiling + 0.1, z: thresholdCentreZ },
      CONCRETE_DARK,
    ),
    shellBox(
      'wall-threshold-west',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(CULL_THRESHOLD.minZ, CULL_THRESHOLD.maxZ) },
      { x: -CULL_THRESHOLD.halfX - wallHalf, y: ceiling / 2, z: thresholdCentreZ },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-threshold-east',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(CULL_THRESHOLD.minZ, CULL_THRESHOLD.maxZ) },
      { x: CULL_THRESHOLD.halfX + wallHalf, y: ceiling / 2, z: thresholdCentreZ },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-threshold-end',
      { x: CULL_THRESHOLD.halfX + wallThickness, y: ceiling / 2, z: wallHalf },
      { x: 0, y: ceiling / 2, z: CULL_THRESHOLD.maxZ + wallHalf },
      CONCRETE_WALL,
    ),
  );

  return solids;
}

/**
 * Four point lights, and the fourth one is the room.
 *
 * D-027 binds this hardware by lights per fragment rather than by fittings, and
 * the arithmetic here is deliberately mean: two cold worklights over the near
 * end that die by mid-corridor, one cold spill inside the bay that she is never
 * in, and one red lamp at the far end that is the only thing lighting the third
 * of the room she has to cross.
 *
 * **The brightest light in the room is the one she is not in**, which is the
 * whole picture in a sentence.
 */
export function cullCorridorFixtures(options: CullCorridorOptions = {}): Fixture[] {
  if (options.returnState) {
    // The hardware count stays fixed. The return is brighter because the same
    // five practicals serve the whole run, not because it quietly adds a costly
    // new lighting variant; the red fitting remains dressed but no longer owns
    // the room's last third.
    return RETURN_WORKLIGHTS.map(({ x, y, z }) => ({
      kind: 'point' as const,
      position: { x, y: (y ?? CULL.ceiling) - 0.15, z },
      intensity: 7,
      range: 7.5,
    }));
  }
  return [
    { kind: 'point', position: { x: 0, y: CULL.ceiling - 0.15, z: -11.0 }, intensity: 7, range: 7.5 },
    { kind: 'point', position: { x: 0, y: CULL.ceiling - 0.15, z: -7.2 }, intensity: 5, range: 7 },
    // Inside the bay, behind the sheeting, aimed at nothing. It is what makes
    // the shapes silhouettes rather than objects.
    { kind: 'point', position: { x: CULL_BAY.minX + 1.2, y: 2.2, z: 0.2 }, intensity: 11, range: 8, name: 'bay-spill' },
    {
      kind: 'point',
      position: { x: CULL_LAMP_AT.x - 0.25, y: CULL_LAMP_AT.y, z: CULL_LAMP_AT.z },
      colour: zone.accent ?? 0xc02b1f,
      // Strong for a 0.28 m fitting, because it is doing the work of a room's
      // worth of lighting in the last eight metres, and because red at low
      // intensity in 0.35 haze reads as brown.
      intensity: 9,
      range: 9,
      name: 'red-lamp',
    },
    // At the far end of the passage beyond the door, and it is the last thing
    // in Act 1's first half: cold, ordinary, and somewhere else. The whole of
    // the carried shot is spent leaving one lamp and approaching this one.
    { kind: 'point', position: { x: 0, y: CULL.ceiling - 0.4, z: CULL_THRESHOLD.maxZ - 1.2 }, intensity: 4, range: 6 },
  ];
}

/**
 * The room's bed: an extractor somewhere behind the bay.
 *
 * **The machine's own cycle is deliberately NOT here.** `plant-cycle` is a loop
 * whose period is the beat's clock, and the sheeting's lift has to land on the
 * same 4.2 s as the sound. A bed started by the room at scene assembly and a
 * timer started by the director at the beat's first stage drift apart by however
 * long the boot took, so the director owns both. See `CullDirector`.
 */
export function cullCorridorAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [{ id: 'extractor-fan', at: { x: CULL_BAY.maxX, y: 2.4, z: CULL_BAY.maxZ }, gain: 0.4 }];
}

/** Where each shape hangs. One list, so the rail and the shapes cannot disagree. */
function shapePlacements(): Placement[] {
  const placements: Placement[] = [];
  for (let i = 0; i < CULL_RAIL.count; i++) {
    placements.push({
      x: CULL_RAIL.x,
      y: CULL_RAIL.shapeOriginY,
      z: CULL_RAIL.minZ + i * CULL_RAIL.pitch,
    });
  }
  return placements;
}

/**
 * The ordinary work run, one list for lit fittings and point lights alike.
 *
 * A return light with no tube below it is a bare game light; a tube with no
 * source is decorative geometry. Keeping their positions inseparable prevents
 * either version of that lie when this state is tuned again.
 */
const RETURN_WORKLIGHTS: readonly Placement[] = [
  { x: 0, y: CULL.ceiling, z: -10.5 },
  { x: 0, y: CULL.ceiling, z: -5.5 },
  { x: 0, y: CULL.ceiling, z: -0.5 },
  { x: 0, y: CULL.ceiling, z: 4.5 },
  { x: 0, y: CULL.ceiling, z: CULL_THRESHOLD.maxZ - 1.2 },
];

/** The handles beat 5 needs. Everything else in the room is scenery. */
export interface CullCorridorParts {
  group: THREE.Group;
  /**
   * The hanging plastic, rebuilt when the lift changes.
   *
   * Rebuilt rather than animated, exactly as beat 1's strip curtain was: the
   * factory is pure, five panels cost nothing, and reaching into an asset's
   * construction from a director is what the asset seam exists to prevent.
   */
  sheeting: THREE.Group;
  /** The bin she leaves in. The director moves it; nothing else may. */
  bin: THREE.Object3D;
}

/**
 * Dress the shell.
 *
 * Everything repeated goes through `instanceGroup`, including the single-placement
 * pieces, for the reason `serviceCorridor.ts` gives: it keeps a second one a
 * content edit rather than a quiet return to per-mesh assembly.
 */
export function dressCullCorridor(): CullCorridorParts;
export function dressCullCorridor(options: { readonly returnState: true }): { group: THREE.Group };
export function dressCullCorridor(options: CullCorridorOptions = {}): CullCorridorParts | { group: THREE.Group } {
  const group = new THREE.Group();
  group.name = 'cull-corridor-dressing';

  // Only over the near end. The middle and far thirds of this room have no
  // ceiling fitting at all, and the gap is the reason the red lamp reads as a
  // destination rather than as decoration.
  const ceilingTubes = instanceGroup(
    assetOrThrow('ceiling-tube'),
    options.returnState ? RETURN_WORKLIGHTS : [-11.0, -7.2].map((z) => ({ x: 0, y: CULL.ceiling, z })),
    options.returnState ? 'cull-return-worklights' : 'cull-ceiling-tubes',
  );
  if (options.returnState) ceilingTubes.userData['worklightCount'] = RETURN_WORKLIGHTS.length;
  group.add(ceilingTubes);

  const rail = assetDefinitionOrThrow('overhead-rail');
  // Its own stated top face determines the rail's mounting offset. A copied
  // 0.215 m here would silently put the rail at the wrong height when the asset
  // changes, which is exactly the kind of separate geometry-and-placement truth
  // the asset contract is meant to prevent.
  group.add(instanceGroup(
    rail.create(),
    [{
      x: CULL_RAIL.x,
      y: CULL_RAIL.y - rail.footprint.height,
      z: CULL_RAIL.minZ + ((CULL_RAIL.count - 1) * CULL_RAIL.pitch) / 2,
    }],
    'cull-rail',
  ));
  if (!options.returnState) {
    group.add(instanceGroup(assetOrThrow('hanging-shape'), shapePlacements(), 'cull-shapes'));
  }

  // ── Both of these are turned a quarter turn, and that is why they read oddly ──
  //
  // `rotY = -π/2` maps the asset's local **+Z to world −X**, so an asset that is
  // centred on its own depth — which `docs/asset-conventions.md` requires and
  // which both of these now are — has to be backed off along **X**, not Z, to
  // leave it where it was. Getting that axis wrong moves a wall fitting into the
  // corridor rather than into the wall, and it is invisible in the code either
  // way; it is only visible in a frame.
  //
  // Both offsets exist so the world result is unchanged by the contract fix. The
  // constants below keep meaning what they always meant: `CULL_MACHINE_AT` is
  // where the housing stands, `CULL_LAMP_AT` is the wall face the lamp mounts on.
  const machine = assetDefinitionOrThrow('plant-machine');
  group.add(instanceGroup(
    machine.create(),
    [{ x: CULL_MACHINE_AT.x - MACHINE_CENTRE_OFFSET, z: CULL_MACHINE_AT.z, rotY: -Math.PI / 2 }],
    'cull-machine',
  ));

  // Mounted at height rather than standing on the floor: `docs/asset-conventions.md`
  // authors every fitting at ground contact, so hanging one is a placement, never
  // a second authored variant. Half the lamp's own declared depth is read off the
  // asset rather than restated, for the reason the rail above gives.
  const lamp = assetDefinitionOrThrow('bulkhead-lamp');
  group.add(instanceGroup(
    lamp.create(),
    [{
      x: CULL_LAMP_AT.x - lamp.footprint.length / 2,
      y: CULL_LAMP_AT.y,
      z: CULL_LAMP_AT.z,
      rotY: -Math.PI / 2,
    }],
    'cull-red-lamp',
  ));

  // Beat 8 walks back out through this doorway, so the return draws the leaf
  // standing open into the corridor. Until session 70 it drew the beat 5 door
  // shut across a doorway with no solid in it, and she walked through the
  // leaf — `docs/playthrough-audit.md` row 23. The hinge is turned on the
  // source because `instanceGroup` bakes sub-mesh transforms into the merge.
  // Gate: the doorway raycast in `blackout-return.spec.ts`, watched red with
  // the leaf shut.
  const door = assetOrThrow('personnel-door');
  if (options.returnState) {
    const hinge = door.getObjectByName('door-hinge');
    if (!hinge) throw new Error('cull corridor: the personnel door has no rigged leaf');
    hinge.rotation.y = CULL_RETURN_DOOR_OPEN;
  }
  group.add(instanceGroup(door, [{ x: CULL_DOOR_AT.x, z: CULL_DOOR_AT.z }], 'cull-door'));

  if (options.returnState) {
    // The bay is ordinary work space now. These are existing wash-down kit
    // parts, placed against the back wall rather than inventing a new cleaning
    // prop for a single return; the bare floor and absent rail load are the rest
    // of the evidence that the bay has been cleared.
    group.add(instanceGroup(
      assetOrThrow('hose-reel'),
      [{ x: CULL_BAY.maxX - 0.06, y: 1.1, z: spanCentre(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ), rotY: -Math.PI / 2 }],
      'cull-return-hose-reel',
    ));
    group.add(instanceGroup(
      assetOrThrow('hose-nozzle'),
      [{
        x: CULL_BAY.maxX - 0.4,
        z: spanCentre(spanCentre(CULL_BAY.mouthMinZ, CULL_BAY.mouthMaxZ), CULL_BAY.mouthMaxZ),
        rotY: Math.PI / 2,
      }],
      'cull-return-hose-nozzle',
    ));
    return { group };
  }

  // ── The two things the director touches ───────────────────────────────────
  const sheeting = instanceGroup(plasticSheeting(0), [{ x: 0, z: 0 }], 'cull-sheeting');
  sheeting.position.set(SHEETING_POSE.x, SHEETING_POSE.y, SHEETING_POSE.z);
  sheeting.rotation.y = SHEETING_POSE.rotY;
  group.add(sheeting);

  // ── The bin hangs off a pivot, because it has to fall over ────────────────
  //
  // `binPivot` sits on the bin's DOWNSTREAM bottom edge — the one it topples
  // about — and the bin is offset back onto its own footprint inside it. So
  // `pivot.rotation.x` is the topple, in radians, and 0 is upright; nothing else
  // in the beat has to know where the axis of rotation is.
  //
  // An offset child rather than a re-authored mesh, for `race-gate`'s reason:
  // `docs/asset-conventions.md` requires the reusable asset's origin to be its
  // ground contact, and a bin authored around its tipping edge would fail that
  // contract to serve one beat.
  const binPivot = new THREE.Group();
  binPivot.name = 'cull-bin-pivot';
  binPivot.position.set(CULL_BIN_AT.x, 0, CULL_BIN_AT.z + CULL_BIN.tipPivotDz);
  const bin = assetOrThrow('wheeled-bin');
  bin.name = 'cull-bin';
  bin.position.set(0, 0, -CULL_BIN.tipPivotDz);
  bin.rotation.y = Math.PI / 2;
  binPivot.add(bin);
  group.add(binPivot);

  return { group, sheeting, bin: binPivot };
}

/**
 * Where the sheeting hangs and which way it faces.
 *
 * Exported as a pose rather than as a builder because the director rebuilds the
 * panels itself — it imports `plasticSheeting(lift)` the same way `bornDirector`
 * imports `stripCurtain(sway)` — and what it must not have to know is where in
 * the room the thing lives. Moving the mouth moves the sheeting.
 */
export const SHEETING_POSE = { x: CULL.maxX, y: 0, z: 0, rotY: -Math.PI / 2 } as const;

/** Build the whole room. Director state deliberately lives elsewhere. */
export function createCullCorridor(): CullCorridorParts;
export function createCullCorridor(options: { readonly returnState: true }): { group: THREE.Group };
export function createCullCorridor(options: CullCorridorOptions = {}): CullCorridorParts | { group: THREE.Group } {
  const room = new THREE.Group();
  room.name = 'cull-corridor';
  room.add(solidMeshes(cullCorridorSolids(options), 'shell'));
  if (options.returnState) {
    room.add(dressCullCorridor({ returnState: true }).group);
    return { group: room };
  }
  const dressing = dressCullCorridor();
  room.add(dressing.group);
  return { group: room, sheeting: dressing.sheeting, bin: dressing.bin };
}
